import React, { useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingUp,
  Security,
  AccountBalanceWallet,
  ArrowForward,
  PlayArrow,
  Star,
  Rocket,
  FlashOn,
  Verified,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { colors, typography, spacing, borderRadius, transitions, componentStyles } from '../../styles/designSystem';

const useStyles = makeStyles((theme) => ({
  hero: {
    ...componentStyles.hero,
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
  },
  
  container: {
    ...componentStyles.contentContainer,
    position: 'relative',
    zIndex: 2,
  },
  
  mainContent: {
    textAlign: 'center',
    marginBottom: spacing['4xl'],
  },
  
  heroTitle: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['7xl'],
    lineHeight: typography.lineHeight.tight,
    marginBottom: spacing.xl,
    background: colors.gradients.primary,
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    letterSpacing: typography.letterSpacing.tight,
    [theme.breakpoints.down('md')]: {
      fontSize: typography.fontSize['5xl'],
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: typography.fontSize['4xl'],
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize['8xl'],
    },
    [theme.breakpoints.up('xxl')]: {
      fontSize: typography.fontSize['9xl'],
    },
  },
  
  heroSubtitle: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize['2xl'],
    lineHeight: typography.lineHeight.relaxed,
    color: colors.text.secondary,
    marginBottom: spacing['2xl'],
    maxWidth: '900px',
    margin: '0 auto',
    letterSpacing: typography.letterSpacing.normal,
    [theme.breakpoints.down('md')]: {
      fontSize: typography.fontSize.xl,
    },
    [theme.breakpoints.up('xl')]: {
      fontSize: typography.fontSize['3xl'],
      maxWidth: '1100px',
    },
    [theme.breakpoints.up('xxl')]: {
      fontSize: typography.fontSize['4xl'],
      maxWidth: '1300px',
    },
  },
  
  ctaSection: {
    display: 'flex',
    gap: spacing.lg,
    justifyContent: 'center',
    flexWrap: 'wrap',
    marginBottom: spacing['3xl'],
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'center',
    },
  },
  
  primaryButton: {
    ...componentStyles.button.primary,
    fontSize: typography.fontSize.lg,
    padding: `${spacing.lg} ${spacing['2xl']}`,
    borderRadius: borderRadius['2xl'],
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
      transition: 'left 0.6s ease',
    },
    '&:hover::before': {
      left: '100%',
    },
  },
  
  secondaryButton: {
    ...componentStyles.button.outline,
    fontSize: typography.fontSize.lg,
    padding: `${spacing.lg} ${spacing['2xl']}`,
    borderRadius: borderRadius['2xl'],
    border: `2px solid ${colors.primary.main}`,
    color: colors.primary.main,
    background: 'transparent',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '0%',
      height: '100%',
      background: colors.primary.main,
      transition: 'width 0.4s ease',
      zIndex: -1,
    },
    '&:hover': {
      color: colors.text.primary,
      '&::before': {
        width: '100%',
      },
    },
  },
  
  featuresGrid: {
    marginTop: spacing['4xl'],
  },
  
  featureCard: {
    ...componentStyles.glassCard,
    textAlign: 'center',
    padding: spacing['2xl'],
    height: '100%',
    minHeight: '280px', // Add consistent minimum height
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'radial-gradient(circle at center, rgba(230, 0, 122, 0.1) 0%, transparent 70%)',
      opacity: 0,
      transition: 'opacity 0.3s ease',
    },
    '&:hover::before': {
      opacity: 1,
    },
  },
  
  featureIcon: {
    fontSize: '3rem',
    color: colors.primary.main,
    marginBottom: spacing.lg,
    transition: transitions.smooth,
  },
  
  featureTitle: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.bold,
    fontSize: typography.fontSize.xl,
    color: colors.text.primary,
    marginBottom: spacing.md,
    letterSpacing: typography.letterSpacing.wide,
  },
  
  featureDescription: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.regular,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    lineHeight: typography.lineHeight.relaxed,
  },
  
  floatingElements: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: 'none',
    zIndex: 1,
  },
  
  floatingElement: {
    position: 'absolute',
    opacity: 0.1,
    animation: '$float 6s ease-in-out infinite',
  },
  
  '@keyframes float': {
    '0%, 100%': {
      transform: 'translateY(0px) rotate(0deg)',
    },
    '50%': {
      transform: 'translateY(-20px) rotate(180deg)',
    },
  },
  
  statsSection: {
    marginTop: spacing['4xl'],
    padding: `${spacing['2xl']} 0`,
    background: colors.gradients.glass,
    borderRadius: borderRadius['2xl'],
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    backdropFilter: 'blur(20px)',
  },
  
  statItem: {
    textAlign: 'center',
    padding: spacing.lg,
  },
  
  statNumber: {
    fontFamily: typography.fontFamily.primary,
    fontWeight: typography.fontWeight.black,
    fontSize: typography.fontSize['4xl'],
    color: colors.primary.main,
    marginBottom: spacing.sm,
    letterSpacing: typography.letterSpacing.tight,
  },
  
  statLabel: {
    fontFamily: typography.fontFamily.secondary,
    fontWeight: typography.fontWeight.medium,
    fontSize: typography.fontSize.base,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: typography.letterSpacing.wider,
  },
}));

const ModernHero = () => {
  const classes = useStyles();

  const controls = useAnimation();
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const handleTradeNow = () => {
    // TODO: Implement trade functionality
    console.log('Trade Now clicked');
  };

  const handleLearnMore = () => {
    // TODO: Implement learn more functionality
    console.log('Learn More clicked');
  };

  const features = [
    {
      icon: <Security />,
      title: 'Secure & Audited',
      description: 'Smart contracts audited by leading security firms',
    },
    {
      icon: <TrendingUp />,
      title: 'High Performance',
      description: 'Optimized for maximum trading efficiency',
    },
    {
      icon: <Verified />,
      title: 'Verified Token',
      description: 'Fully verified and transparent tokenomics',
    },
    {
      icon: <AccountBalanceWallet />,
      title: 'Multi-Wallet Support',
      description: 'Connect with your preferred wallet',
    },
  ];

  const stats = [
    { number: '1B+', label: 'Total Supply' },
    { number: '50K+', label: 'Holders' },
    { number: '$2M+', label: 'Market Cap' },
    { number: '99.9%', label: 'Uptime' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  const floatingVariants = {
    animate: {
      y: [-10, 10, -10],
      rotate: [0, 180, 360],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: 'easeInOut',
      },
    },
  };

  return (
    <Box className={classes.hero}>
      {/* Floating Elements */}
      <Box className={classes.floatingElements}>
        <motion.div
          className={classes.floatingElement}
          style={{ top: '10%', left: '10%' }}
          variants={floatingVariants}
          animate="animate"
        >
          <Star style={{ fontSize: '2rem', color: colors.primary.main }} />
        </motion.div>
        <motion.div
          className={classes.floatingElement}
          style={{ top: '20%', right: '15%' }}
          variants={floatingVariants}
          animate="animate"
          transition={{ delay: 1 }}
        >
          <Rocket style={{ fontSize: '1.5rem', color: colors.secondary.main }} />
        </motion.div>
        <motion.div
          className={classes.floatingElement}
          style={{ bottom: '30%', left: '20%' }}
          variants={floatingVariants}
          animate="animate"
          transition={{ delay: 2 }}
        >
          <FlashOn style={{ fontSize: '1.8rem', color: colors.primary.light }} />
        </motion.div>
      </Box>

      <Container className={classes.container}>
        <motion.div
          ref={ref}
          initial="hidden"
          animate={controls}
          variants={containerVariants}
        >
          <Box className={classes.mainContent}>
            <motion.div variants={itemVariants}>
              <Typography className={classes.heroTitle}>
                Retro Llama Token
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Typography className={classes.heroSubtitle}>
                Experience the future of decentralized trading with our innovative token platform. 
                Built for security, performance, and community-driven growth.
              </Typography>
            </motion.div>
            
            <motion.div variants={itemVariants}>
              <Box className={classes.ctaSection}>
                <Button
                  variant="contained"
                  className={classes.primaryButton}
                  onClick={handleTradeNow}
                  startIcon={<TrendingUp />}
                  endIcon={<ArrowForward />}
                >
                  Trade Now
                </Button>
                <Button
                  variant="outlined"
                  className={classes.secondaryButton}
                  onClick={handleLearnMore}
                  startIcon={<PlayArrow />}
                >
                  Learn More
                </Button>
              </Box>
            </motion.div>
          </Box>

          {/* Stats Section */}
          <motion.div variants={itemVariants}>
            <Box className={classes.statsSection}>
              <Grid container spacing={3}>
                {stats.map((stat, index) => (
                  <Grid item xs={6} md={3} key={index}>
                    <Box className={classes.statItem}>
                      <Typography className={classes.statNumber}>
                        {stat.number}
                      </Typography>
                      <Typography className={classes.statLabel}>
                        {stat.label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </motion.div>

          {/* Features Grid */}
          <motion.div variants={itemVariants}>
            <Box className={classes.featuresGrid}>
              <Grid container spacing={4} style={{ alignItems: 'stretch' }}>
                {features.map((feature, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index} style={{ display: 'flex' }}>
                    <motion.div
                      whileHover={{ 
                        scale: 1.05,
                        transition: { duration: 0.3 }
                      }}
                      whileTap={{ scale: 0.95 }}
                      style={{ width: '100%', display: 'flex' }}
                    >
                      <Card className={classes.featureCard}>
                        <CardContent>
                          <Box className={classes.featureIcon}>
                            {feature.icon}
                          </Box>
                          <Typography className={classes.featureTitle}>
                            {feature.title}
                          </Typography>
                          <Typography className={classes.featureDescription}>
                            {feature.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </motion.div>
        </motion.div>
      </Container>
    </Box>
  );
};

export default ModernHero; 